import React, { useState, useEffect } from 'react';
import axios from '../../../../AxiosInstance';
import Modal from '../../../../components/modal/modal';
import styles from './scheduler.module.css';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faPlus, faTrash, faPen } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from 'react-i18next';

const COMMON_TIMEZONES = [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Lima',
    'America/Bogota',
    'America/Mexico_City',
    'America/Santiago',
    'America/Buenos_Aires',
    'America/Sao_Paulo',
    'Europe/London',
    'Europe/Madrid',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Asia/Kolkata',
    'Asia/Dubai',
    'Australia/Sydney',
    'Pacific/Auckland',
];

export default function Scheduler({ show, onClose, projectId, operationGroupId }) {
    const { t } = useTranslation();

    const DAYS = [
        { value: 0, label: t('operations.scheduler.days.mon') },
        { value: 1, label: t('operations.scheduler.days.tue') },
        { value: 2, label: t('operations.scheduler.days.wed') },
        { value: 3, label: t('operations.scheduler.days.thu') },
        { value: 4, label: t('operations.scheduler.days.fri') },
        { value: 5, label: t('operations.scheduler.days.sat') },
        { value: 6, label: t('operations.scheduler.days.sun') },
    ];

    const FREQUENCY_LABELS = {
        weekly: t('operations.scheduler.frequency.weekly'),
        daily: t('operations.scheduler.frequency.daily'),
        interval: t('operations.scheduler.frequency.interval'),
        once: t('operations.scheduler.frequency.once'),
    };

    const [schedules, setSchedules] = useState([]);
    const [minIntervalMinutes, setMinIntervalMinutes] = useState(1);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        frequency: 'daily',
        hour: 9,
        minute: 0,
        days_of_week: '0,1,2,3,4',
        interval_minutes: 60,
        specific_date: '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        is_active: true,
    });

    useEffect(() => {
        if (show && projectId) {
            fetchSchedules();
        }
    }, [show, projectId]);

    const fetchSchedules = () => {
        axios.get(`action_schedule/?project_id=${projectId}`).then((response) => {
            const data = response.data;
            setSchedules(data.schedules || []);
            setMinIntervalMinutes(data.min_interval_minutes || 1);
        }).catch((err) => {
            console.error('Error fetching schedules:', err);
        });
    };

    const handleDayToggle = (dayValue) => {
        const currentDays = formData.days_of_week ? formData.days_of_week.split(',').map(Number) : [];
        let newDays;
        if (currentDays.includes(dayValue)) {
            newDays = currentDays.filter(d => d !== dayValue);
        } else {
            newDays = [...currentDays, dayValue].sort();
        }
        setFormData({ ...formData, days_of_week: newDays.join(',') });
    };

    const handleSave = () => {
        const payload = {
            ...formData,
            project: projectId,
            operation_group: operationGroupId,
        };

        // Enforce min interval
        if (payload.frequency === 'interval' && payload.interval_minutes < minIntervalMinutes) {
            payload.interval_minutes = minIntervalMinutes;
        }

        // Convert specific_date string to ISO format if present
        if (payload.frequency === 'once' && payload.specific_date) {
            payload.specific_date = new Date(payload.specific_date).toISOString();
        } else {
            payload.specific_date = null;
        }

        const request = editingId
            ? axios.put(`action_schedule/${editingId}/`, payload)
            : axios.post('action_schedule/', payload);

        request.then(() => {
            fetchSchedules();
            resetForm();
        }).catch((err) => {
            console.error('Error saving schedule:', err);
        });
    };

    const handleEdit = (schedule) => {
        setFormData({
            name: schedule.name,
            frequency: schedule.frequency,
            hour: schedule.hour,
            minute: schedule.minute,
            days_of_week: schedule.days_of_week,
            interval_minutes: schedule.interval_minutes,
            specific_date: schedule.specific_date ? schedule.specific_date.slice(0, 16) : '',
            timezone: schedule.timezone || 'UTC',
            is_active: schedule.is_active,
        });
        setEditingId(schedule.id);
        setShowForm(true);
    };

    const handleDelete = (id) => {
        axios.delete(`action_schedule/${id}/`).then(() => {
            fetchSchedules();
        }).catch((err) => {
            console.error('Error deleting schedule:', err);
        });
    };

    const handleToggleActive = (schedule) => {
        axios.put(`action_schedule/${schedule.id}/`, {
            ...schedule,
            is_active: !schedule.is_active,
        }).then(() => {
            fetchSchedules();
        }).catch((err) => {
            console.error('Error toggling schedule:', err);
        });
    };

    const resetForm = () => {
        setFormData({
            name: '',
            frequency: 'daily',
            hour: 9,
            minute: 0,
            days_of_week: '0,1,2,3,4',
            interval_minutes: Math.max(60, minIntervalMinutes),
            specific_date: '',
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
            is_active: true,
        });
        setEditingId(null);
        setShowForm(false);
    };

    const handleIntervalChange = (value) => {
        const parsed = parseInt(value) || minIntervalMinutes;
        setFormData({ ...formData, interval_minutes: Math.max(parsed, minIntervalMinutes) });
    };

    const selectedDays = formData.days_of_week ? formData.days_of_week.split(',').map(Number) : [];

    const getScheduleDescription = (schedule) => {
        const tz = schedule.timezone && schedule.timezone !== 'UTC' ? ` (${schedule.timezone})` : '';
        switch (schedule.frequency) {
            case 'weekly':
                return `${String(schedule.hour).padStart(2, '0')}:${String(schedule.minute).padStart(2, '0')} - ${schedule.days_of_week.split(',').map(d => DAYS[parseInt(d)]?.label).join(', ')}${tz}`;
            case 'daily':
                return t('operations.scheduler.everyDayAt', { time: `${String(schedule.hour).padStart(2, '0')}:${String(schedule.minute).padStart(2, '0')}`, tz });
            case 'interval':
                return t('operations.scheduler.everyMinutesDesc', { min: schedule.interval_minutes });
            case 'once':
                return schedule.specific_date ? t('operations.scheduler.once', { date: new Date(schedule.specific_date).toLocaleString(), tz }) : t('operations.scheduler.onceNoDate');
            default:
                return schedule.frequency;
        }
    };

    return (
        <Modal show={show} setter={onClose} name={t('operations.scheduler.title')} hideCancel={true}>
            <div className={styles.container}>
                {schedules.length === 0 && !showForm && (
                    <div className={styles.emptyState}>
                        <FontAwesomeIcon icon={faCalendarAlt} size="2x" color="#94A3B8" />
                        <p>{t('operations.scheduler.noSchedules')}</p>
                        <p style={{ fontSize: '12px', color: '#94A3B8', marginTop: '-8px' }}>
                            {t('operations.scheduler.schedulesDesc')}
                        </p>
                    </div>
                )}

                {schedules.map((schedule) => (
                    <div key={schedule.id} className={styles.scheduleItem}>
                        <div className={styles.scheduleHeader}>
                            <div className={styles.scheduleName}>{schedule.name}</div>
                            <div className={styles.scheduleActions}>
                                <label className={styles.toggleSwitch}>
                                    <input
                                        type="checkbox"
                                        checked={schedule.is_active}
                                        onChange={() => handleToggleActive(schedule)}
                                    />
                                    <span className={styles.toggleSlider}></span>
                                </label>
                                <FontAwesomeIcon
                                    icon={faPen}
                                    color="#64748B"
                                    className={styles.iconButton}
                                    onClick={() => handleEdit(schedule)}
                                />
                                <FontAwesomeIcon
                                    icon={faTrash}
                                    color="#E11D48"
                                    className={styles.iconButton}
                                    onClick={() => handleDelete(schedule.id)}
                                />
                            </div>
                        </div>
                        <div className={styles.scheduleDetail}>
                            <span className={styles.frequencyBadge}>
                                {FREQUENCY_LABELS[schedule.frequency] || schedule.frequency}
                            </span>
                            <span className={styles.scheduleDays}>
                                {getScheduleDescription(schedule)}
                            </span>
                        </div>
                    </div>
                ))}

                {/* Add/Edit Form */}
                {showForm && (
                    <div className={styles.formContainer}>
                        <div className={styles.formTitle}>
                            {editingId ? t('operations.scheduler.editSchedule') : t('operations.scheduler.newSchedule')}
                        </div>

                        <div className={styles.formField}>
                            <label>{t('operations.scheduler.nameLabel')}</label>
                            <input
                                className={styles.formInput}
                                placeholder={t('operations.scheduler.nameLabel')}
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>

                        <div className={styles.formField}>
                            <label>{t('operations.scheduler.frequency')}</label>
                            <select
                                className={styles.formSelect}
                                value={formData.frequency}
                                onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                            >
                                <option value="daily">{t('operations.scheduler.frequencyOptions.daily')}</option>
                                <option value="weekly">{t('operations.scheduler.frequencyOptions.weekly')}</option>
                                <option value="interval">{t('operations.scheduler.frequencyOptions.interval')}</option>
                                <option value="once">{t('operations.scheduler.frequencyOptions.once')}</option>
                            </select>
                        </div>

                        {/* Timezone selector for time-based frequencies */}
                        {(formData.frequency === 'daily' || formData.frequency === 'weekly' || formData.frequency === 'once') && (
                            <div className={styles.formField}>
                                <label>{t('operations.scheduler.timezone')}</label>
                                <select
                                    className={styles.formSelect}
                                    value={formData.timezone}
                                    onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                                >
                                    {COMMON_TIMEZONES.map((tz) => (
                                        <option key={tz} value={tz}>{tz}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Time fields for daily/weekly/once */}
                        {(formData.frequency === 'daily' || formData.frequency === 'weekly' || formData.frequency === 'once') && (
                            <div className={styles.formField}>
                                <label>{t('operations.scheduler.time')}</label>
                                <div className={styles.timeInputs}>
                                    <input
                                        type="number"
                                        className={styles.formInputSmall}
                                        min="0"
                                        max="23"
                                        value={formData.hour}
                                        onChange={(e) => setFormData({ ...formData, hour: parseInt(e.target.value) || 0 })}
                                    />
                                    <span>:</span>
                                    <input
                                        type="number"
                                        className={styles.formInputSmall}
                                        min="0"
                                        max="59"
                                        value={formData.minute}
                                        onChange={(e) => setFormData({ ...formData, minute: parseInt(e.target.value) || 0 })}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Days for weekly */}
                        {formData.frequency === 'weekly' && (
                            <div className={styles.formField}>
                                <label>{t('operations.scheduler.days')}</label>
                                <div className={styles.daysContainer}>
                                    {DAYS.map((day) => (
                                        <div
                                            key={day.value}
                                            className={`${styles.dayChip} ${selectedDays.includes(day.value) ? styles.dayChipActive : ''}`}
                                            onClick={() => handleDayToggle(day.value)}
                                        >
                                            {day.label}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Interval minutes */}
                        {formData.frequency === 'interval' && (
                            <div className={styles.formField}>
                                <label>{t('operations.scheduler.everyMinutes', { min: minIntervalMinutes })}</label>
                                <input
                                    type="number"
                                    className={styles.formInput}
                                    min={minIntervalMinutes}
                                    value={formData.interval_minutes}
                                    onChange={(e) => handleIntervalChange(e.target.value)}
                                />
                            </div>
                        )}

                        {/* Specific date for once */}
                        {formData.frequency === 'once' && (
                            <div className={styles.formField}>
                                <label>{t('operations.scheduler.dateAndTime')}</label>
                                <input
                                    type="datetime-local"
                                    className={styles.formInput}
                                    value={formData.specific_date}
                                    onChange={(e) => setFormData({ ...formData, specific_date: e.target.value })}
                                />
                            </div>
                        )}

                        <div className={styles.formButtons}>
                            <button className={styles.cancelButton} onClick={resetForm}>{t('common.cancel')}</button>
                            <button className={styles.saveButton} onClick={handleSave}>
                                {editingId ? t('common.update') : t('common.create')}
                            </button>
                        </div>
                    </div>
                )}

                {/* Add Button */}
                {!showForm && (
                    <div className={styles.addButton} onClick={() => setShowForm(true)}>
                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                        {t('operations.scheduler.addSchedule')}
                    </div>
                )}
            </div>
        </Modal>
    );
}
